import React, { useEffect, useState } from 'react';
import { Form } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Button } from 'components/Atom/Button';
import Select from 'components/Atom/Select';
import Input from 'components/Atom/Input';
import { useSelector } from 'react-redux';
import { addPricingModel, updatePricingModel, getAllMeters } from 'api/subscriptions';
import { notification } from 'antd';
import { SUB_CurrencyNames } from 'utils/services';
import { chargeTypes, pricingTypes } from 'utils/constant';
import './assets/style.css';

const typeOptions = [
  { label: 'One-time', value: 'one-time' },
  { label: 'Recurring', value: 'recurring' },
];

const chargeTypeOptions = [
  { label: 'Standard', value: chargeTypes.STANDARD },
  { label: 'Tiered', value: chargeTypes.TIERED },
  { label: 'Volume', value: chargeTypes.VOLUME },
  { label: 'Percentage', value: chargeTypes.PERCENTAGE },
  { label: 'Composite', value: chargeTypes.COMPOSITE },
];

const pricingTypeOptions = [
  { label: 'Standard', value: pricingTypes.STANDARD },
  { label: 'Tiered', value: pricingTypes.TIERED },
  { label: 'Volume', value: pricingTypes.VOLUME },
  { label: 'Percentage', value: pricingTypes.PERCENTAGE },
  { label: 'Fixed Retainer', value: pricingTypes.FIXED },
];

const tieredVolumeChargeTypes = [chargeTypes.TIERED, chargeTypes.VOLUME];
const tieredVolumePricingTypes = [pricingTypes.TIERED, pricingTypes.VOLUME];

const getChargeTypeOptions = (behavior) => (behavior === 'recurring' ? chargeTypeOptions?.filter((opt) => !tieredVolumeChargeTypes?.includes(opt?.value)) : chargeTypeOptions);

const getPricingTypeOptions = (behavior) => (behavior === 'recurring' ? pricingTypeOptions?.filter((opt) => !tieredVolumePricingTypes?.includes(opt?.value)) : pricingTypeOptions);

const normalizeNumber = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  return Number(value);
};

const parseTierValue = (value) => {
  if (value === 'Infinity' || value === Infinity) return Infinity;
  if (value === undefined || value === null || value === '') return undefined;
  return Number(value);
};

const normalizeUpperLimit = (value, isLastTier) => {
  if (value === 'Infinity' || value === Infinity) return 'Infinity';
  if (isLastTier && (value === undefined || value === null || value === '')) return 'Infinity';
  return normalizeNumber(value);
};

export const CreatePricingModel = ({ handleOpen, refetch, meterData, isEdit }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meterOptions, setMeterOptions] = useState([]);
  const store = useSelector((state) => state?.company?.store);
  const userStores = useSelector((state) => state?.company?.userStores);
  const behavior = Form.useWatch('type', form);
  const chargeType = Form.useWatch('charge_type', form);
  const components = Form.useWatch('components', form);
  const chargeTypeOptionsForBehavior = getChargeTypeOptions(behavior);
  const pricingTypeOptionsForBehavior = getPricingTypeOptions(behavior);
  const isMobile = window.innerWidth < 768;
  const twoColumnGrid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 };

  useEffect(() => {
    if (isEdit && meterData) {
      form.setFieldsValue({
        name: meterData?.name,
        type: meterData?.type,
        charge_type: meterData?.charge_type,
        currency: meterData?.currency,
        baseAmount: meterData?.baseAmount,
        combination_operator: meterData?.combination_operator,
        or_strategy: meterData?.or_strategy,
        components:
          meterData?.components?.map((component) => ({
            meter_id: component?.meter_id,
            pricing_type: component?.pricing_type,
            pricing_schema: component?.pricing_schema,
          })) || [],
      });
    } else {
      form.setFieldsValue({
        name: '',
        type: undefined,
        charge_type: undefined,
        currency: undefined,
        baseAmount: undefined,
        combination_operator: undefined,
        or_strategy: undefined,
        components: [
          {
            meter_id: undefined,
            pricing_type: undefined,
            pricing_schema: {},
          },
        ],
      });
    }
  }, [isEdit, meterData, form]);

  useEffect(() => {
    const fetchMeterList = async () => {
      if (!store?.isSelected || isEdit) return;
      const response = await getAllMeters({ store_id: store?._id, offset: 0, limit: 1000 });
      const items = response?.data?.data?.billingMeters || [];
      const activeMeters = items?.filter((item) => item?.active === true);
      setMeterOptions(
        activeMeters?.map((item) => ({
          label: item?.name ? `${item?.name} / ${item?.id}` : item?.id,
          value: item?.id,
        })),
      );
    };
    fetchMeterList();
  }, [store, isEdit]);

  useEffect(() => {
    if (behavior !== 'recurring') return;

    const updates = {};
    const currentChargeType = form.getFieldValue('charge_type');
    if (tieredVolumeChargeTypes.includes(currentChargeType)) {
      updates.charge_type = undefined;
    }

    const currentComponents = form.getFieldValue('components') || [];
    const nextComponents = currentComponents?.map((component) =>
      tieredVolumePricingTypes.includes(component?.pricing_type) ? { ...component, pricing_type: undefined, pricing_schema: {} } : component,
    );
    if (nextComponents?.some((component, index) => component !== currentComponents[index])) {
      updates.components = nextComponents;
    }

    if (Object.keys(updates)?.length > 0) {
      form.setFieldsValue(updates);
    }
  }, [behavior, form]);

  const transformPricingSchema = (pricingType, pricingSchema) => {
    if (!pricingSchema) return {};

    if (pricingType === pricingTypes.STANDARD || pricingType === pricingTypes.FIXED) {
      return { amount: normalizeNumber(pricingSchema?.amount) };
    }

    if (pricingType === pricingTypes.PERCENTAGE) {
      return {
        percentage: normalizeNumber(pricingSchema?.percentage),
        flatFee: normalizeNumber(pricingSchema?.flatFee),
      };
    }

    if (pricingType === pricingTypes.TIERED || pricingType === pricingTypes.VOLUME) {
      const tiers = pricingSchema?.tiers || [];
      return tiers?.map((tier, index) => ({
        lowerLimit: normalizeNumber(tier?.lowerLimit),
        upperLimit: normalizeUpperLimit(tier?.upperLimit, index === tiers?.length - 1),
        pricePerUnit: normalizeNumber(tier?.pricePerUnit),
        flatFee: normalizeNumber(tier?.flatFee),
      }));
    }

    return pricingSchema;
  };

  const chargeToPricingType = {
    [chargeTypes.STANDARD]: pricingTypes.STANDARD,
    [chargeTypes.TIERED]: pricingTypes.TIERED,
    [chargeTypes.VOLUME]: pricingTypes.VOLUME,
    [chargeTypes.PERCENTAGE]: pricingTypes.PERCENTAGE,
  };

  // Keep pricing_type in sync with charge_type when not composite,
  // and enforce component count for composite charge type.
  useEffect(() => {
    const ct = form.getFieldValue('charge_type');
    if (!ct) return;

    const currentComponents = form.getFieldValue('components') || [];

    if (ct === chargeTypes.COMPOSITE) {
      // Ensure at least two components present for composite
      if (currentComponents?.length < 2) {
        const first = currentComponents[0] || { meter_id: undefined, pricing_type: pricingTypes.STANDARD, pricing_schema: { amount: undefined } };
        const second = currentComponents[1] || { meter_id: undefined, pricing_type: pricingTypes.STANDARD, pricing_schema: { amount: undefined } };
        form.setFieldsValue({ components: [first, second] });
      }
      return;
    }

    // For non-composite charge types: force single component and set pricing_type
    const mappedPricing = chargeToPricingType[ct] || pricingTypes.STANDARD;
    let pricing_schema = { amount: undefined };
    if (mappedPricing === pricingTypes.TIERED || mappedPricing === pricingTypes.VOLUME) {
      pricing_schema = { tiers: [{ lowerLimit: undefined, upperLimit: undefined, pricePerUnit: undefined, flatFee: undefined }] };
    }
    const newComponent = (currentComponents[0] && { ...currentComponents[0], pricing_type: mappedPricing, pricing_schema }) || { meter_id: undefined, pricing_type: mappedPricing, pricing_schema };
    form.setFieldsValue({ components: [newComponent] });
  }, [chargeType, form]);

  useEffect(() => {
    if (!components?.length) return;

    let changed = false;
    const nextComponents = components?.map((component) => {
      const pricingType = component?.pricing_type;
      const pricingSchema = component?.pricing_schema || {};

      if (pricingType === pricingTypes.TIERED || pricingType === pricingTypes.VOLUME) {
        const tiers = pricingSchema?.tiers || [];
        if (tiers?.length === 0) {
          changed = true;
          return {
            ...component,
            pricing_schema: {
              ...pricingSchema,
              tiers: [{ lowerLimit: 1, upperLimit: 'Infinity', pricePerUnit: undefined, flatFee: undefined }],
            },
          };
        }

        const adjustedTiers = tiers?.map((tier, tierIndex) => {
          const isFirstTier = tierIndex === 0;
          const isLastTier = tierIndex === tiers?.length - 1;

          let lowerLimit = tier?.lowerLimit;
          if (isFirstTier && (lowerLimit === undefined || lowerLimit === null || lowerLimit === '')) {
            lowerLimit = 1;
            changed = true;
          }

          let upperLimit = tier?.upperLimit;
          if (isLastTier && (upperLimit === undefined || upperLimit === null || upperLimit === '')) {
            upperLimit = 'Infinity';
            changed = true;
          }
          if (!isLastTier && (upperLimit === 'Infinity' || upperLimit === Infinity)) {
            upperLimit = undefined;
            changed = true;
          }

          return {
            ...tier,
            lowerLimit,
            upperLimit,
          };
        });

        return {
          ...component,
          pricing_schema: {
            ...pricingSchema,
            tiers: adjustedTiers,
          },
        };
      }

      return component;
    });

    if (changed) {
      form.setFieldsValue({ components: nextComponents });
    }
  }, [components, form]);

  const onAddSubmit = async (values) => {
    if (userStores?.length > 1 && !store?.isSelected && !isEdit) {
      notification.error({ message: 'Select store from the drop-down menu above', placement: 'bottomRight' });
      return;
    }

    // If editing, only allow updating the name field per requirement
    const payload = isEdit
      ? { name: values?.name }
      : {
          name: values?.name,
          type: values?.type,
          charge_type: values?.charge_type,
          currency: values?.currency,
          baseAmount: normalizeNumber(values?.baseAmount),
          ...(values?.charge_type === chargeTypes.COMPOSITE
            ? {
                combination_operator: values?.combination_operator,
                ...(values?.or_strategy ? { or_strategy: values?.or_strategy } : {}),
              }
            : {}),
          components: (values?.components || [])?.map((component) => ({
            meter_id: component?.meter_id,
            pricing_type: component?.pricing_type,
            pricing_schema: transformPricingSchema(component?.pricing_type, component?.pricing_schema),
          })),
        };

    setIsSubmitting(true);
    try {
      const response = isEdit ? await updatePricingModel(meterData?.id, payload) : await addPricingModel(store?._id, payload);
      if (response?.data?.success || response?.status === 200) {
        handleOpen();
        refetch();
        form.resetFields();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSchemaFields = (field) => {
    const pricingType = form.getFieldValue(['components', field.name, 'pricing_type']);
    if (!pricingType) return null;

    if (pricingType === pricingTypes.STANDARD || pricingType === pricingTypes.FIXED) {
      return (
        <Form.Item
          name={[field.name, 'pricing_schema', 'amount']}
          label='Amount'
          className='customInput mobileInputView steric-after w-[49%]'
          validateFirst
          rules={[
            { required: true, message: 'Amount is required' },
            {
              validator: (_, value) => (value === undefined || value === null || value === '' ? Promise.resolve() : Number(value) > 0 ? Promise.resolve() : Promise.reject('Amount must be positive')),
            },
          ]}>
          <Input type='number' placeholder='Enter amount' />
        </Form.Item>
      );
    }

    if (pricingType === pricingTypes.PERCENTAGE) {
      return (
        <div style={twoColumnGrid}>
          <Form.Item
            name={[field.name, 'pricing_schema', 'percentage']}
            label='Percentage'
            className='customInput mobileInputView steric-after'
            validateFirst
            rules={[
              { required: true, message: 'Percentage is required' },
              {
                validator: (_, value) =>
                  value === undefined || value === null || value === '' ? Promise.resolve() : Number(value) > 0 ? Promise.resolve() : Promise.reject('Percentage must be positive'),
              },
            ]}>
            <Input type='number' placeholder='Enter percentage' />
          </Form.Item>
          <Form.Item
            name={[field.name, 'pricing_schema', 'flatFee']}
            label='Flat Fee'
            className='customInput mobileInputView'
            rules={[
              {
                validator: (_, value) => (value === undefined || value === null || value === '' || Number(value) >= 0 ? Promise.resolve() : Promise.reject('Flat fee must be non-negative')),
              },
            ]}>
            <Input type='number' placeholder='Enter flat fee' />
          </Form.Item>
        </div>
      );
    }

    if (pricingType === pricingTypes.TIERED || pricingType === pricingTypes.VOLUME) {
      return (
        <Form.List name={[field.name, 'pricing_schema', 'tiers']}>
          {(tierFields, { add, remove }) => {
            const tiers = form.getFieldValue(['components', field.name, 'pricing_schema', 'tiers']) || [];
            return (
              <div className='tier-list'>
                {tierFields?.map((tierField, tierIndex) => {
                  const isFirstTier = tierIndex === 0;
                  const isLastTier = tierIndex === tiers?.length - 1;
                  return (
                    <div key={tierField.key} className='tier-item'>
                      <div className='tier-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className='tier-title'>Tier {tierIndex + 1}</span>
                        {tierFields?.length > 1 && <DeleteOutlined onClick={() => remove(tierField.name)} style={{ color: 'red', cursor: 'pointer', fontSize: 18 }} />}
                      </div>
                      <div className='create-form' style={twoColumnGrid}>
                        <Form.Item
                          name={[tierField.name, 'lowerLimit']}
                          label='Lower Limit'
                          className='customInput mobileInputView steric-after'
                          validateFirst
                          rules={[
                            { required: true, message: 'Lower limit is required' },
                            {
                              validator: (_, value) => {
                                const numericValue = parseTierValue(value);
                                if (numericValue === undefined) return Promise.reject('Lower limit is required');
                                if (!Number.isInteger(numericValue) || numericValue <= 0) return Promise.reject('Must be positive integer');
                                if (isFirstTier) {
                                  return numericValue === 1 ? Promise.resolve() : Promise.reject('First tier lower limit must be 1');
                                }
                                const prevUpper = parseTierValue(tiers?.[tierIndex - 1]?.upperLimit);
                                if (prevUpper === undefined || prevUpper === Infinity) return Promise.resolve();
                                return numericValue === prevUpper + 1 ? Promise.resolve() : Promise.reject(`Must be ${prevUpper + 1}`);
                              },
                            },
                          ]}>
                          <Input type='number' placeholder={isFirstTier ? '1' : 'Enter lower limit'} disabled={isFirstTier} />
                        </Form.Item>
                        <Form.Item
                          name={[tierField.name, 'upperLimit']}
                          label='Upper Limit'
                          className='customInput mobileInputView steric-after'
                          validateFirst
                          rules={[
                            { required: true, message: 'Upper limit is required' },
                            {
                              validator: (_, value) => {
                                const upperValue = value === 'Infinity' || value === Infinity ? Infinity : parseTierValue(value);
                                if (upperValue === undefined) return Promise.reject('Upper limit is required');
                                if (isLastTier) {
                                  return upperValue === Infinity ? Promise.resolve() : Promise.reject('Must be Infinity');
                                }
                                if (upperValue === Infinity) {
                                  return Promise.reject('Only last tier can be Infinity');
                                }
                                if (!Number.isInteger(upperValue) || upperValue <= 0) return Promise.reject('Must be positive integer');
                                const lowerValue = parseTierValue(form.getFieldValue(['components', field.name, 'pricing_schema', 'tiers', tierIndex, 'lowerLimit']));
                                return lowerValue !== undefined && upperValue <= lowerValue ? Promise.reject('Upper limit must be greater than lower limit') : Promise.resolve();
                              },
                            },
                          ]}>
                          <Input placeholder={isLastTier ? 'Infinity' : 'Enter upper limit'} disabled={isLastTier} />
                        </Form.Item>
                        <Form.Item
                          name={[tierField.name, 'pricePerUnit']}
                          label='Price Per Unit'
                          className='customInput mobileInputView steric-after'
                          validateFirst
                          rules={[
                            { required: true, message: 'Price per unit is required' },
                            {
                              validator: (_, value) =>
                                value === undefined || value === null || value === ''
                                  ? Promise.resolve()
                                  : Number(value) >= 0
                                    ? Promise.resolve()
                                    : Promise.reject('Price per unit cannot be negative'),
                            },
                          ]}>
                          <Input type='number' placeholder='Enter price per unit' />
                        </Form.Item>
                        <Form.Item
                          name={[tierField.name, 'flatFee']}
                          label='Flat Fee'
                          className='customInput mobileInputView'
                          rules={[
                            {
                              validator: (_, value) =>
                                value === undefined || value === null || value === '' || Number(value) >= 0 ? Promise.resolve() : Promise.reject('Flat fee must be non-negative'),
                            },
                          ]}>
                          <Input type='number' placeholder='Enter flat fee' />
                        </Form.Item>
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    className='rounded-lg button-theme'
                    style={{ width: 100 }}
                    onClick={() => {
                      const tiersPath = ['components', field.name, 'pricing_schema', 'tiers'];
                      const currentTiers = form.getFieldValue(tiersPath) || [];
                      if (currentTiers?.length > 0) {
                        const prevLastIndex = currentTiers?.length - 1;
                        form.setFieldValue([...tiersPath, prevLastIndex, 'upperLimit'], undefined);
                      }
                      add({ lowerLimit: undefined, upperLimit: undefined, pricePerUnit: undefined, flatFee: undefined });
                    }}>
                    + Tier
                  </Button>
                </div>
              </div>
            );
          }}
        </Form.List>
      );
    }

    return null;
  };

  return (
    <div className='formDetails'>
      <Form form={form} layout='vertical' onFinish={onAddSubmit}>
        {isEdit ? (
          <Form.Item
            name='name'
            label='Name'
            className='customInput mobileInputView steric-after'
            rules={[
              { required: true, message: 'Name is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const regex = /^[^%?{}[\]$^@<>"&!:;~*]+$/;
                  if (regex.test(value)) return Promise.resolve();
                  return Promise.reject('%?{}[]$^@<>"&!:;~* are not allowed.');
                },
              },
            ]}>
            <Input type='string' placeholder='Enter name' name='name' />
          </Form.Item>
        ) : (
          (() => {
            const isMobile = window.innerWidth < 768;
            const gridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 };
            return (
              <>
                <div className='create-form' style={gridStyle}>
                  <Form.Item
                    name='name'
                    label='Name'
                    className='customInput mobileInputView steric-after'
                    rules={[
                      { required: true, message: 'Name is required' },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const regex = /^[^%?{}[\]$^@<>"&!:;~*]+$/;
                          if (regex.test(value)) return Promise.resolve();
                          return Promise.reject('%?{}[]$^@<>"&!:;~* are not allowed.');
                        },
                      },
                    ]}>
                    <Input type='string' placeholder='Enter name' name='name' />
                  </Form.Item>

                  <Form.Item name='type' label='Model Type' className='customInput mobileInputView steric-after' rules={[{ required: true, message: 'Model type is required' }]}>
                    <Select optionsValue={typeOptions} labelInValue={false} placeholder='Select model type' />
                  </Form.Item>

                  <Form.Item name='currency' label='Currency' className='customInput mobileInputView steric-after' rules={[{ required: true, message: 'Currency is required' }]}>
                    <Select optionsValue={SUB_CurrencyNames} labelInValue={false} placeholder='Select currency' />
                  </Form.Item>

                  <Form.Item
                    name='baseAmount'
                    label='Base Amount'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        validator: (_, value) => (value === undefined || value === null || value === '' || Number(value) > 0 ? Promise.resolve() : Promise.reject('Base amount must be positive')),
                      },
                    ]}>
                    <Input type='number' placeholder='Enter base amount (optional)' />
                  </Form.Item>

                  <Form.Item name='charge_type' label='Charge Type' className='customInput mobileInputView steric-after' rules={[{ required: true, message: 'Charge type is required' }]}>
                    <Select optionsValue={chargeTypeOptionsForBehavior} labelInValue={false} placeholder='Select charge type' />
                  </Form.Item>

                  {chargeType === chargeTypes.COMPOSITE && (
                    <>
                      <Form.Item
                        name='combination_operator'
                        label='Combination Operator'
                        className='customInput mobileInputView steric-after'
                        rules={[{ required: true, message: 'Combination operator is required' }]}>
                        <Select
                          optionsValue={[
                            { label: 'AND', value: 'AND' },
                            { label: 'OR', value: 'OR' },
                          ]}
                          labelInValue={false}
                          placeholder='Select operator'
                        />
                      </Form.Item>
                      <Form.Item shouldUpdate={(prev, cur) => prev.combination_operator !== cur.combination_operator} noStyle>
                        {() =>
                          form.getFieldValue('combination_operator') === 'OR' ? (
                            <Form.Item
                              name='or_strategy'
                              label='Whichever  Strategy'
                              className='customInput mobileInputView steric-after'
                              rules={[{ required: true, message: 'Whichever  strategy is required' }]}>
                              <Select
                                optionsValue={[
                                  { label: 'Higher', value: 'higher' },
                                  { label: 'Lower', value: 'lower' },
                                ]}
                                labelInValue={false}
                                placeholder='Select whichever  strategy'
                              />
                            </Form.Item>
                          ) : null
                        }
                      </Form.Item>
                    </>
                  )}
                </div>
                <div>
                  <Form.List name='components'>
                    {(fields, { add, remove }) => (
                      <>
                        {fields?.map((field, index) => (
                          <div key={field.key} className='component-card'>
                            <div className='detail-heading'>
                              <h3>{chargeType === chargeTypes.COMPOSITE ? `Component ${index + 1}` : 'Component'}</h3>
                            </div>
                            <div className='transaction-modal component-fields-row'>
                              <Form.Item
                                name={[field.name, 'meter_id']}
                                label='Meter ID'
                                className='customInput mobileInputView steric-after'
                                rules={[{ required: true, message: 'Meter is required' }]}>
                                <Select
                                  optionsValue={meterOptions}
                                  labelInValue={false}
                                  placeholder='Select Meter'
                                  showSearch
                                  optionFilterProp='label'
                                  getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') || document.body}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, 'pricing_type']}
                                label='Pricing Type'
                                className='customInput mobileInputView steric-after'
                                rules={[{ required: true, message: 'Pricing type is required' }]}>
                                <Select
                                  optionsValue={pricingTypeOptionsForBehavior}
                                  labelInValue={false}
                                  placeholder='Select pricing type'
                                  disabled={chargeType !== chargeTypes.COMPOSITE}
                                  getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') || document.body}
                                />
                              </Form.Item>
                            </div>
                            <Form.Item style={{ marginTop: 16 }} shouldUpdate={(prev, cur) => prev.components?.[field.name]?.pricing_type !== cur.components?.[field.name]?.pricing_type}>
                              {() => renderSchemaFields(field)}
                            </Form.Item>
                          </div>
                        ))}
                      </>
                    )}
                  </Form.List>
                </div>
              </>
            );
          })()
        )}

        <div className='mb-[-20px] form_button update-button'>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <Button block className='rounded-lg bg-plain' onClick={handleOpen}>
                Cancel
              </Button>
              <Button block className='button-theme ml-4 rounded-lg' htmlType='submit' loading={isSubmitting}>
                {isEdit ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default CreatePricingModel;
